import { stylesToRecord } from '@webbuilder/style-engine'
import { componentStates, type InteractionStates } from './interactionStates'
import type { PageTheme, ProjectTheme } from './types'

/**
 * Los estilos del tema, como variables CSS de la página publicada.
 *
 * Al publicar, lo que un elemento toma de un estilo del tema —la letra de un
 * título, el radio y la sombra de un botón, el fondo de una tarjeta— se escribía
 * con su valor suelto en la regla de CADA elemento. Cambiar de tema obligaba a
 * rehacer la hoja entera.
 *
 * Ahora ese valor va envuelto: `border-radius: var(--wb-t-b-button-primary-border-radius, 12px)`.
 * El valor de hoy viaja dentro, de respaldo, así que la página se ve igual
 * mientras nadie defina la variable —no hace falta declararlas al publicar—. Y
 * quien quiera cambiar el tema en una página ya abierta sólo define esas
 * variables en `:root` (`themeTokenVarDeclarations`): un cambio de estilo es un
 * puñado de variables, sin tocar el HTML ni las reglas.
 *
 * El nombre sale del estilo y de la propiedad, y es el mismo en los dos lados:
 * quien publica (`tokenVarFor` en el editor) y quien cambia el tema.
 */

export type TokenKind = 'button' | 'typography' | 'card' | 'section' | 'icon' | 'role'
export type TokenRef = { kind: TokenKind; id: string }
export type TokenBreakpoint = 'desktop' | 'tablet' | 'mobile'

/**
 * Lo que cambia de un tema a otro y se puede cambiar sin recolocar nada: letra,
 * color, forma y aire. Fuera lo que es de la maqueta (display, tamaños, rejilla):
 * eso es del bloque, no del estilo.
 */
export const TOKEN_VAR_PROPS: ReadonlySet<string> = new Set([
  'fontFamily', 'fontWeight', 'fontSize', 'fontStyle', 'lineHeight', 'letterSpacing', 'textTransform', 'textDecoration',
  'color', 'background', 'backgroundColor',
  'border', 'borderWidth', 'borderStyle', 'borderColor',
  'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
  'borderRadius', 'boxShadow', 'backdropFilter', 'opacity',
  'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'gap',
])

const KIND_CODE: Record<TokenKind, string> = { button: 'b', typography: 'y', card: 'c', section: 's', icon: 'i', role: 'r' }

const COLLECTION: Record<Exclude<TokenKind, 'role'>, 'buttonComponents' | 'typographyComponents' | 'cardComponents' | 'sectionComponents' | 'iconComponents'> = {
  button: 'buttonComponents',
  typography: 'typographyComponents',
  card: 'cardComponents',
  section: 'sectionComponents',
  icon: 'iconComponents',
}

type StyledToken = { id: string; styles?: Record<string, unknown>; breakpoints?: Record<string, Record<string, unknown> | undefined> }

const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
const kebab = (prop: string) => prop.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)

/** El nombre de la variable de una propiedad de un estilo, en un tamaño de pantalla. */
export function tokenVarName(ref: TokenRef, prop: string, breakpoint: TokenBreakpoint = 'desktop'): string {
  return `--wb-t-${KIND_CODE[ref.kind]}-${slug(ref.id)}-${kebab(prop)}${breakpoint === 'desktop' ? '' : `-${breakpoint[0]}`}`
}

/** Lo que un estilo del tema pone en un tamaño de pantalla: lo de escritorio con lo propio de ese tamaño encima. */
export function tokenStylesAt(theme: PageTheme | ProjectTheme, ref: TokenRef, breakpoint: TokenBreakpoint): Record<string, string> {
  if (ref.kind === 'role') {
    const roles = theme.roles as Record<string, Record<string, unknown> | undefined>
    const overrides = breakpoint === 'desktop' ? undefined : (theme.roleBreakpoints as Record<string, Record<string, Record<string, unknown> | undefined> | undefined> | undefined)?.[breakpoint]?.[ref.id]
    return stylesToRecord({ ...(roles[ref.id] ?? {}), ...(overrides ?? {}) } as never)
  }
  const list = (theme as Partial<ProjectTheme>)[COLLECTION[ref.kind]] as StyledToken[] | undefined
  const token = Array.isArray(list) ? list.find((item) => item.id === ref.id) : undefined
  if (!token) return {}
  const overrides = breakpoint === 'desktop' ? undefined : token.breakpoints?.[breakpoint]
  return stylesToRecord({ ...(token.styles ?? {}), ...(overrides ?? {}) } as never)
}

/**
 * La variable de una propiedad de un estilo en un tamaño de pantalla, con su
 * valor. En un tamaño que dice lo mismo que escritorio se usa la de escritorio:
 * así no hay tres variables para un valor que es uno. `null` si el estilo no
 * pone esa propiedad o no es de las que viajan como variable.
 */
export function tokenVarFor(
  theme: PageTheme | ProjectTheme,
  ref: TokenRef,
  prop: string,
  breakpoint: TokenBreakpoint,
): { name: string; value: string } | null {
  if (!TOKEN_VAR_PROPS.has(prop)) return null
  const value = tokenStylesAt(theme, ref, breakpoint)[prop]
  if (value === undefined || value === '') return null
  const sameAsDesktop = breakpoint === 'desktop' || tokenStylesAt(theme, ref, 'desktop')[prop] === value
  return { name: tokenVarName(ref, prop, sameAsDesktop ? 'desktop' : breakpoint), value }
}

/* ── Los estados: al pasar el ratón, al pulsar, al enfocar ───────────────────
   Los pone el estilo del botón o de la tarjeta, y al publicar iban con su valor
   suelto en cada elemento: con otro tema puesto en la página, el botón volvía
   al diseño de antes al pasarle el ratón. Ahora viajan igual que lo demás. */

export const TOKEN_STATES = ['hover', 'pressed', 'focus'] as const
export type TokenState = (typeof TOKEN_STATES)[number]

/** Lo que un estado puede cambiar: lo de siempre y lo propio de un gesto (subir, brillar, el aro de enfoque). */
export const TOKEN_STATE_PROPS: ReadonlySet<string> = new Set([
  ...TOKEN_VAR_PROPS,
  'transform', 'filter', 'outline', 'outlineColor', 'outlineOffset', 'outlineWidth', 'outlineStyle',
])

/** El nombre de la variable de una propiedad en un estado de un estilo. */
export function tokenStateVarName(ref: TokenRef, state: TokenState, prop: string): string {
  return `--wb-t-${KIND_CODE[ref.kind]}-${slug(ref.id)}-${state}-${kebab(prop)}`
}

/** Los estados de un estilo de botón o de tarjeta: los suyos, o los de su papel si no guardó ninguno. */
export function tokenStatesOf(theme: PageTheme | ProjectTheme, ref: TokenRef): InteractionStates {
  if (ref.kind !== 'button' && ref.kind !== 'card') return {}
  const list = (theme as Partial<ProjectTheme>)[COLLECTION[ref.kind]] as (StyledToken & { role?: string; states?: InteractionStates })[] | undefined
  const token = Array.isArray(list) ? list.find((item) => item.id === ref.id) : undefined
  return componentStates(token as never, ref.kind)
}

/**
 * El valor de una propiedad de un estado, envuelto para publicar.
 *
 * Dos respaldos encadenados: la variable del estado; si el tema que haya puesto
 * no dice nada de esa propiedad en ese estado, la variable de la propiedad en
 * reposo —el botón se queda como está, en vez de volver al valor del tema con
 * que se publicó—; y si no hay ninguna (la página tal como se publicó), el
 * valor de hoy.
 */
export function wrapTokenStateValue(ref: TokenRef, state: TokenState, prop: string, value: string): string {
  if (!TOKEN_STATE_PROPS.has(prop)) return value
  const rest = TOKEN_VAR_PROPS.has(prop) ? `var(${tokenVarName(ref, prop)},${value})` : value
  return `var(${tokenStateVarName(ref, state, prop)},${rest})`
}

/** Todos los estilos del tema que pueden dar variables. */
export function themeTokenRefs(theme: PageTheme | ProjectTheme): TokenRef[] {
  const refs: TokenRef[] = Object.keys(theme.roles ?? {}).map((id) => ({ kind: 'role' as const, id }))
  for (const kind of Object.keys(COLLECTION) as Exclude<TokenKind, 'role'>[]) {
    const list = (theme as Partial<ProjectTheme>)[COLLECTION[kind]] as StyledToken[] | undefined
    if (Array.isArray(list)) refs.push(...list.map((item) => ({ kind, id: item.id })))
  }
  return refs
}

/**
 * Las variables de un tema entero: lo que hay que poner en `:root` para que
 * una página publicada con otro tema se vea con éste. Nombre → valor.
 *
 * Sólo las de escritorio y, de los demás tamaños, las que dicen otra cosa: las
 * mismas que usa quien publicó (`tokenVarFor`).
 */
export function themeTokenVarDeclarations(theme: PageTheme | ProjectTheme): Record<string, string> {
  const out: Record<string, string> = {}
  for (const ref of themeTokenRefs(theme)) {
    for (const breakpoint of ['desktop', 'tablet', 'mobile'] as const) {
      for (const prop of Object.keys(tokenStylesAt(theme, ref, breakpoint))) {
        const variable = tokenVarFor(theme, ref, prop, breakpoint)
        if (variable) out[variable.name] = variable.value
      }
    }
    /* Y sus estados (botones y tarjetas). */
    const states = tokenStatesOf(theme, ref)
    for (const state of TOKEN_STATES) {
      for (const [prop, value] of Object.entries(stylesToRecord((states[state] ?? {}) as never))) {
        if (TOKEN_STATE_PROPS.has(prop) && value !== '') out[tokenStateVarName(ref, state, prop)] = value
      }
    }
  }
  return out
}
